import { computed } from 'vue';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import matches from 'validator/lib/matches';
import { hasValidMobilePhoneNumber, isString, ssnPattern } from '@/validators';
import { useLabel } from './useLabel';

type TFn = (key: string, params?) => string;

type ValidationResult = true | string;
type Validator = (value) => ValidationResult;
type ValidatorFactory = (label: string) => Validator;

interface ValidationRules {
  required: Validator;
  requiredField: ValidatorFactory;
  requiredCheck: ValidatorFactory;
  requiredSelection: ValidatorFactory;
  pep: Validator;
  isNotEmpty: Validator;
  email: Validator;
  pid: Validator;
  pidEmpty: Validator;
  mobilePhoneNumber: Validator;
  tristateBooleanRequired: Validator;
}

export function useValidationRules(t?: TFn) {
  const resolvedT: TFn = t ?? useLabel().t;

  const rules = computed((): ValidationRules => {
    const _t = resolvedT;
    return {
      required: (v) => !!v || _t('validation.required'),
      requiredField:
        (label: string): Validator =>
        (v) =>
          !!v || _t('validation.requiredField', { property: label }),
      requiredCheck:
        (label: string): Validator =>
        (v) =>
          !!v || _t('validation.requiredCheck', { property: label }),
      requiredSelection:
        (label: string): Validator =>
        (v) =>
          !!v || _t('validation.requiredSelection', { property: label }),
      pep: (v) => (!v && v != null) || _t('validation.pep'),
      isNotEmpty: (v) =>
        (isString(v) && !isEmpty(v, { ignore_whitespace: true })) || _t('validation.required'),
      email: (v) => !v || (typeof v === 'string' && isEmail(v)) || _t('validation.email'),
      pid: (v) => !isString(v) || matches(v, ssnPattern) || _t('validation.pid'),
      pidEmpty: (v) => !isString(v) || v === '' || matches(v, ssnPattern) || _t('validation.pid'),
      mobilePhoneNumber: (v) =>
        !v ||
        (typeof v === 'string' && hasValidMobilePhoneNumber(v)) ||
        _t('validation.mobileNumber'),
      tristateBooleanRequired: (v) => (v !== null && v !== undefined) || _t('validation.required'),
    };
  });

  return { rules } as const;
}

export default useValidationRules;
